if (!customElements.get('tank-bundles-no')) {
  customElements.define('tank-bundles-no', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.productInfo = this.closest('product-info');
      this.form = document.getElementById(this.dataset.formId);
      this.variants = JSON.parse(this.querySelector('[data-tank-bundle-variants]').textContent);
      this.variantsComplete = this.variants.length >= Number(this.dataset.variantCount || this.variants.length);
      this.loadStatus = this.querySelector('[data-tank-bundle-load-status]');
      this.retryButton = this.querySelector('[data-tank-bundle-retry]');
      this.colorIndex = Number(this.dataset.colorIndex);
      this.sizeIndex = Number(this.dataset.sizeIndex);
      this.packIndex = Number(this.dataset.packIndex);
      this.cards = [...this.querySelectorAll('[data-tank-bundle-card]')];
      this.colorPreview = this.productInfo.querySelector('[data-tank-bundle-color-preview]');
      const selected = this.variants.find(variant => String(variant.id) === this.dataset.selectedId &&
        this.cards.some(card => card.dataset.pack === variant.options[this.packIndex]));
      this.state = {
        color: selected?.options[this.colorIndex] || this.dataset.initialColor,
        size: selected?.options[this.sizeIndex] || this.dataset.initialSize,
        pack: selected?.options[this.packIndex] || this.dataset.initialPack,
      };
      this.singlePack = this.cards.find(card => Number(card.dataset.quantity) === 1)?.dataset.pack;
      this.rowsByPack = new Map(this.cards.map(card => [card.dataset.pack,
        Array.from({ length: Number(card.dataset.quantity) || 1 }, () => ({
          color: this.state.color, size: this.state.size,
        })),
      ]));
      this.focusedIndex = 0;
      this.submitting = false;
      this.onChange = this.handleChange.bind(this);
      this.onFocus = this.handleFocus.bind(this);
      this.onColorClick = this.handleColorClick.bind(this);
      this.onSubmit = this.handleSubmit.bind(this);
      this.onRetry = this.loadRemainingVariants.bind(this);
      this.retryButton?.addEventListener('click', this.onRetry);
      this.productInfo.addEventListener('change', this.onChange);
      this.productInfo.addEventListener('focusin', this.onFocus);
      this.productInfo.addEventListener('click', this.onColorClick);
      this.form?.addEventListener('submit', this.onSubmit, true);
      this.render(false);
      if (!this.variantsComplete) this.loadRemainingVariants();
    }

    disconnectedCallback() {
      this.productInfo?.removeEventListener('change', this.onChange);
      this.productInfo?.removeEventListener('focusin', this.onFocus);
      this.productInfo?.removeEventListener('click', this.onColorClick);
      this.form?.removeEventListener('submit', this.onSubmit, true);
      this.retryButton?.removeEventListener('click', this.onRetry);
      this.loadController?.abort();
      this.loadController = null;
      this.loadingVariants = false;
      this.initialized = false;
    }

    async loadRemainingVariants() {
      if (this.loadingVariants || this.variantsComplete) return;
      this.loadingVariants = true;
      this.loadController = new AbortController();
      const controller = this.loadController;
      if (this.loadStatus) {
        this.loadStatus.hidden = false;
        this.loadStatus.textContent = 'Laster inn alle farger og størrelser…';
      }
      if (this.retryButton) this.retryButton.hidden = true;
      this.render(false);
      try {
        const firstPage = await this.fetchVariantPage(1, 'page', controller.signal);
        const pages = Number(firstPage.pages);
        if (!Number.isInteger(pages) || pages < 1 || pages > 100) throw new Error('Invalid variant pagination');
        const variantsById = new Map(firstPage.variants.map(variant => [String(variant.id), variant]));
        // Three requests at a time keep large catalogs responsive without flooding the storefront.
        for (let page = 2; page <= pages; page += 3) {
          const batch = [];
          for (let current = page; current <= Math.min(page + 2, pages); current++) {
            batch.push(this.fetchVariantPage(current, firstPage.page_param || 'page', controller.signal));
          }
          for (const payload of await Promise.all(batch)) {
            payload.variants.forEach(variant => variantsById.set(String(variant.id), variant));
          }
        }
        if (variantsById.size !== Number(firstPage.total)) throw new Error('Incomplete variant pages');
        if (controller.signal.aborted || !this.isConnected) return;
        this.variants = [...variantsById.values()];
        this.variantsComplete = true;
        this.querySelector('[data-tank-bundle-variants]').textContent = JSON.stringify(this.variants);
        if (this.loadStatus) this.loadStatus.hidden = true;
      } catch (error) {
        if (controller.signal.aborted) return;
        if (this.loadStatus) this.loadStatus.textContent = 'Noen alternativer kunne ikke lastes inn. Prøv på nytt.';
        if (this.retryButton) this.retryButton.hidden = false;
      } finally {
        if (this.loadController === controller) {
          this.loadingVariants = false;
          if (this.isConnected) this.render(false);
        }
      }
    }

    async fetchVariantPage(page, pageParam, signal) {
      const url = new URL(window.location.href);
      url.hash = '';
      url.searchParams.delete('sections');
      url.searchParams.set('section_id', 'tank-variant-data-no');
      url.searchParams.set(pageParam, String(page));
      const response = await fetch(url.toString(), { signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Variant page request failed');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const data = html.querySelector('[data-tank-variant-page]');
      if (!data) throw new Error('Variant page missing');
      const payload = JSON.parse(data.textContent);
      if (String(payload.product_id) !== this.dataset.productId || Number(payload.page) !== page || !Array.isArray(payload.variants)) {
        throw new Error('Invalid variant page');
      }
      return payload;
    }

    findVariant(pack = this.state.pack, color = this.state.color, size = this.state.size) {
      return this.variants.find(variant =>
        variant.options[this.packIndex] === pack &&
        variant.options[this.colorIndex] === color &&
        variant.options[this.sizeIndex] === size
      );
    }

    getRows(pack = this.state.pack) {
      return this.rowsByPack.get(pack) || [];
    }

    getFocusedRow() {
      const rows = this.getRows();
      return rows[this.focusedIndex] || rows[0];
    }

    syncFocusedRow() {
      const row = this.getFocusedRow();
      if (!row) return;
      this.state.color = row.color;
      this.state.size = row.size;
    }

    getSingleVariant(row) {
      return row && this.singlePack ? this.findVariant(this.singlePack, row.color, row.size) : undefined;
    }

    handleFocus(event) {
      const item = event.target.closest?.('[data-tank-bundle-item]');
      const card = item?.closest('[data-tank-bundle-card]');
      if (!card || card.dataset.pack !== this.state.pack || !this.contains(card)) return;
      this.focusedIndex = Number(item.dataset.index) || 0;
      this.syncFocusedRow();
      this.renderColorPreview();
    }

    handleChange(event) {
      const target = event.target;
      let colorSelected = false;
      if (this.colorPreview?.contains(target) && target.matches('input[type="radio"]')) {
        this.getFocusedRow().color = target.value;
        colorSelected = true;
      } else if (this.contains(target) && target.matches('[data-tank-bundle-pack]')) {
        this.state.pack = target.value;
        this.focusedIndex = 0;
      } else if (this.contains(target) && target.matches('[data-tank-bundle-color], [data-tank-bundle-size]')) {
        const card = target.closest('[data-tank-bundle-card]');
        if (card.dataset.pack !== this.state.pack) return;
        this.focusedIndex = Number(target.closest('[data-tank-bundle-item]')?.dataset.index) || 0;
        const row = this.getFocusedRow();
        if (target.matches('[data-tank-bundle-color]')) {
          row.color = target.value;
          colorSelected = true;
        } else {
          row.size = target.value;
        }
      } else {
        return;
      }
      this.syncFocusedRow();
      this.render(true);
      if (colorSelected) this.updateMedia(this.getSingleVariant(this.getFocusedRow()));
    }

    handleColorClick(event) {
      const target = event.target;
      // A checked swatch does not emit change, but should still reveal its photo.
      if (this.colorPreview?.contains(target) && target.matches('input[type="radio"]') &&
          !target.disabled && target.value === this.state.color) {
        this.updateMedia(this.getSingleVariant(this.getFocusedRow()));
      }
    }

    displayColor(color) {
      // Read the translated label from the rendered option; keep the original value for matching.
      const option = [...this.querySelectorAll('[data-tank-bundle-color] option')].find(item => item.value === color);
      return option?.textContent.trim() || color;
    }

    renderColorPreview() {
      if (!this.colorPreview) return;
      const row = this.getFocusedRow();
      const displayColour = this.displayColor(row.color);
      const label = this.getRows().length > 1 ? `${displayColour} — Singlet ${this.focusedIndex + 1}` : displayColour;
      this.colorPreview.querySelector('[data-selected-value]').textContent = label;
      this.colorPreview.querySelectorAll('input[type="radio"]').forEach(input => {
        input.checked = input.value === row.color;
        const available = this.variants.some(variant => variant.available &&
          variant.options[this.packIndex] === this.singlePack &&
          variant.options[this.colorIndex] === input.value);
        input.disabled = this.variantsComplete && !available;
        input.classList.toggle('disabled', input.disabled);
      });
    }

    render(updateUrl) {
      this.cards.forEach(card => {
        const active = card.dataset.pack === this.state.pack;
        const rows = this.getRows(card.dataset.pack);
        const priceVariant = this.findVariant(card.dataset.pack, rows[0].color, rows[0].size);
        const available = rows.every(row => this.getSingleVariant(row)?.available);
        card.classList.toggle('is-selected', active);
        card.classList.toggle('is-unavailable', !available);
        card.querySelector('[data-tank-bundle-pack]').checked = active;
        card.querySelector('[data-tank-bundle-selectors]').hidden = !active;
        card.querySelector('[data-tank-bundle-price]').textContent = priceVariant?.money || '—';
        const compare = card.querySelector('[data-tank-bundle-compare]');
        const hasSaving = priceVariant && priceVariant.compare_at_price > priceVariant.price;
        compare.textContent = hasSaving ? priceVariant.compare_money : '';
        compare.hidden = !hasSaving;
        const label = card.querySelector('[data-tank-bundle-saving]');
        label.textContent = hasSaving ? `−${Math.round((priceVariant.compare_at_price - priceVariant.price) / priceVariant.compare_at_price * 100)} %` : '';
        label.hidden = !hasSaving;
        const availability = card.querySelector('[data-tank-bundle-availability]');
        availability.hidden = available;
        availability.textContent = !this.variantsComplete ? 'Laster inn alternativer…' : this.dataset.soldOut;
        card.querySelectorAll('[data-tank-bundle-item]').forEach(item => {
          const row = rows[Number(item.dataset.index)];
          if (!row) return;
          const colorSelect = item.querySelector('[data-tank-bundle-color]');
          const sizeSelect = item.querySelector('[data-tank-bundle-size]');
          colorSelect.value = row.color;
          sizeSelect.value = row.size;
          item.querySelector('[data-tank-bundle-color-dot]').style.backgroundColor =
            colorSelect.selectedOptions[0]?.dataset.swatch || '#d8d8d8';
          [...sizeSelect.options].forEach(option => {
            const candidate = this.findVariant(this.singlePack, row.color, option.value);
            option.disabled = candidate ? !candidate.available : this.variantsComplete;
            option.textContent = option.value + (candidate?.available || (!candidate && !this.variantsComplete) ? '' : ' — Utsolgt');
          });
          [...colorSelect.options].forEach(option => {
            const colorAvailable = this.variants.some(candidate => candidate.available &&
              candidate.options[this.packIndex] === this.singlePack &&
              candidate.options[this.colorIndex] === option.value);
            option.disabled = this.variantsComplete && !colorAvailable;
          });
        });
      });
      this.renderColorPreview();
      this.activeVariants = this.getRows().map(row => this.getSingleVariant(row));
      this.activeVariant = this.getSingleVariant(this.getFocusedRow());
      const available = this.activeVariants.length > 0 && this.activeVariants.every(variant => variant?.available);
      const idInput = this.form?.querySelector('[name="id"]');
      if (idInput) {
        idInput.value = this.activeVariants[0] ? String(this.activeVariants[0].id) : '';
        idInput.disabled = !available;
      }
      const submit = this.form?.querySelector('[type="submit"]');
      if (submit) {
        submit.disabled = this.submitting || !available;
        const label = submit.querySelector('span');
        if (label) label.textContent = available ? this.dataset.addToCart : this.dataset.unavailable;
      }
      const activeCard = this.cards.find(card => card.dataset.pack === this.state.pack);
      const firstRow = this.getRows()[0];
      const priceVariant = this.findVariant(this.state.pack, firstRow.color, firstRow.size);
      this.productInfo.querySelector('product-form')?.handleErrorMessage?.();
      // The URL represents the offer and first tank. Independent row choices stay in memory.
      if (updateUrl && priceVariant) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', priceVariant.id);
        window.history.replaceState({}, '', url);
        this.productInfo.querySelectorAll('.product__title a').forEach(link => { link.href = url.pathname + url.search; });
      }
      this.querySelector('[data-tank-bundle-status]').textContent = available
        ? `${activeCard?.dataset.packLabel || this.state.pack}: ${this.getRows().map((row, index) => `Singlet ${index + 1}: ${this.displayColor(row.color)}, ${row.size}`).join('; ')}. ${priceVariant?.money || ''}`
        : this.dataset.unavailable;
    }

    updateMedia(variant) {
      const mediaId = variant?.media_id || this.variants.find(candidate =>
        candidate.options[this.colorIndex] === this.state.color && candidate.media_id)?.media_id;
      if (!mediaId) return;
      const gallery = this.productInfo.querySelector('media-gallery');
      const targetId = `${this.dataset.sectionId}-${mediaId}`;
      if (!gallery?.querySelector(`[data-media-id="${targetId}"]`)) return;
      // Select the existing photo without reordering the gallery or moving the customer away from the offer.
      gallery.setActiveMedia?.(targetId, false, { scrollIntoView: false });
    }

    getCartItems() {
      const itemsById = new Map();
      for (const row of this.getRows()) {
        const variant = this.getSingleVariant(row);
        if (!variant?.available) return null;
        const existing = itemsById.get(variant.id);
        if (existing) existing.quantity += 1;
        else itemsById.set(variant.id, { id: variant.id, quantity: 1 });
      }
      return [...itemsById.values()];
    }

    async handleSubmit(event) {
      // Own this form submission before Dawn's single-item handler can run.
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.submitting) return;
      const productForm = this.productInfo.querySelector('product-form');
      const items = this.getCartItems();
      if (!items?.length) {
        productForm?.handleErrorMessage?.(this.dataset.unavailable);
        return;
      }
      this.submitting = true;
      const submit = this.form.querySelector('[type="submit"]');
      const spinner = productForm?.querySelector('.loading__spinner');
      submit.disabled = true;
      submit.setAttribute('aria-disabled', 'true');
      submit.classList.add('loading');
      spinner?.classList.remove('hidden');
      productForm?.handleErrorMessage?.();
      let added = false;
      try {
        const response = await fetch(window.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ items }),
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          throw new Error(typeof result.description === 'string' ? result.description : 'Singletene kunne ikke legges til i handlekurven. Prøv på nytt.');
        }
        added = true;
        // Native cart shows every selected color/size and Shopify's automatic bundle discount.
        window.location.assign(window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`);
      } catch (error) {
        if (!this.isConnected) return;
        productForm?.handleErrorMessage?.(error.message || 'Singletene kunne ikke legges til i handlekurven. Prøv på nytt.');
      } finally {
        // Keep the button locked after a successful add while navigation completes.
        if (!added && this.isConnected) {
          this.submitting = false;
          submit.classList.remove('loading');
          submit.removeAttribute('aria-disabled');
          spinner?.classList.add('hidden');
          submit.disabled = !this.getCartItems()?.length;
        }
      }
    }
  });
}
